/*
 * Spend-based efficiency is an atomic server contract.
 *
 * This module validates and adapts that contract for presentation. It never
 * chooses a spend field and never calculates CPQV or CPCV. If the server did
 * not supply a complete, internally reconciling envelope, the metric is
 * unavailable.
 *
 * Byte-pinned across Cortex and Quantum.
 */

export const SPEND_EFFICIENCY_CONTRACT = "spend_efficiency_v1";
export const SPEND_BASIS = Object.freeze({
  GROSS_CLIENT_BILLED: "GROSS_CLIENT_BILLED",
  /*
   * The client-safe basis token the server will send once it stops emitting
   * internal vocabulary to client viewers. Accepted NOW so this portal works
   * against both the pre-change and post-change backend, and the two can be
   * deployed in either order without a gap where efficiency metrics vanish.
   */
  CLIENT_BILLED: "CLIENT_BILLED",
  NET_WORKING_MEDIA: "NET_WORKING_MEDIA",
});

/*
 * INTERNAL display labels. These are Quantum's vocabulary and the contract's
 * own bookkeeping — they must never be rendered on a client surface, and a
 * client payload will stop carrying them entirely.
 */
export const SPEND_BASIS_LABELS = Object.freeze({
  [SPEND_BASIS.GROSS_CLIENT_BILLED]: "GROSS · CLIENT-BILLED",
  [SPEND_BASIS.CLIENT_BILLED]: "CLIENT-BILLED",
  [SPEND_BASIS.NET_WORKING_MEDIA]: "NET · WORKING MEDIA",
});

/*
 * CLIENT-FACING basis vocabulary. A client is billed the client-billed amount;
 * "gross" only means something as the counterpart to "net", so naming it tells
 * them a second, lower number exists. Net working media has no client label
 * because there is no client-safe way to say it.
 */
export const SPEND_BASIS_CLIENT_LABELS = Object.freeze({
  [SPEND_BASIS.GROSS_CLIENT_BILLED]: "Client-billed",
  [SPEND_BASIS.CLIENT_BILLED]: "Client-billed",
  [SPEND_BASIS.NET_WORKING_MEDIA]: null,
});

/*
 * The client basis token, whichever the server sent. Both spellings mean the
 * same money; only one of them is safe to say out loud.
 */
export const CLIENT_BILLED_SPEND_BASES = Object.freeze([
  SPEND_BASIS.GROSS_CLIENT_BILLED,
  SPEND_BASIS.CLIENT_BILLED,
]);

/*
 * Internal financial vocabulary that must never reach a client surface. Used
 * by the hydration backstop below: a payload cached before the server change,
 * or replayed from a stored report, still carries the old strings.
 */
const INTERNAL_BASIS_TEXT =
  /\bgross\b|\bnet\b|\bmargin\b|\bmark[- ]?up\b|\bfee\b|\brate\s+cards?\b|dynamic cost margin/i;

/**
 * The label a client may read for a spend basis.
 *
 * Server-provided `spendBasisClientLabel` wins. Otherwise the basis TOKEN is
 * mapped — never the internal display label, which is why this cannot simply
 * fall back to `spendBasisLabel`. Returns null when there is no client-safe
 * way to name the basis, and callers render nothing rather than guessing.
 */
export function resolveClientSpendBasisLabel(source) {
  if (!isObject(source)) return null;
  const provided = source.spendBasisClientLabel;
  if (typeof provided === "string" && provided.trim() && !INTERNAL_BASIS_TEXT.test(provided)) {
    return provided.trim();
  }
  return SPEND_BASIS_CLIENT_LABELS[source.spendBasis] ?? null;
}

/**
 * Hydration/cache backstop for free text that may embed the internal basis.
 *
 * Formula strings arrive as prose — "GROSS · CLIENT-BILLED spend ÷ TrueView
 * Views". A payload rendered before the server change, or restored from a
 * cached/hydrated report, still carries them. This rewrites the basis phrase
 * to client vocabulary and, if anything internal survives, drops the string
 * rather than displaying it.
 */
export function toClientSafeBasisText(value) {
  if (typeof value !== "string" || !value.trim()) return null;
  if (!INTERNAL_BASIS_TEXT.test(value)) return value;

  const rewritten = value
    .replace(/GROSS\s*·\s*CLIENT-BILLED/gi, "Client-billed")
    .replace(/CLIENT-BILLED\s*·\s*GROSS/gi, "Client-billed")
    .replace(/\bgross\s*\(client-billed\)/gi, "Client-billed")
    .replace(/\bgross[ -]client[ -]billed\b/gi, "Client-billed")
    .replace(/\bgross\b/gi, "Client-billed");

  return INTERNAL_BASIS_TEXT.test(rewritten) ? null : rewritten;
}

const isObject = (value) =>
  value && typeof value === "object" && !Array.isArray(value);

const finiteOrNull = (value) =>
  typeof value === "number" && Number.isFinite(value) ? value : null;

const rootsFor = (payload) => [
  payload,
  payload?.canonicalReporting,
  payload?.canonicalSummary,
  payload?.canonicalAggregate,
  payload?.raw,
  payload?.raw?.canonicalReporting,
  payload?.raw?.canonicalSummary,
].filter(isObject);

function unavailableReason(metric) {
  const code = String(metric?.unavailableReason?.code || "").toUpperCase();
  if (code === "DENOMINATOR_UNAVAILABLE" || code === "ZERO_DENOMINATOR") {
    return "missing_denominator";
  }
  if (code === "DEFINITION_INCOMPATIBLE") return "definition_incompatible";
  return "unreported";
}

function metricReconciles(metric, spendBasis) {
  if (!isObject(metric) || metric.spendBasis !== spendBasis) return false;
  const value = finiteOrNull(metric.value);
  if (value === null) return metric.available === false;
  const numerator = finiteOrNull(metric.numeratorUnrounded);
  const denominator = finiteOrNull(metric.denominatorCountUnrounded);
  if (numerator === null || denominator === null || denominator <= 0) return false;
  const expected = numerator / denominator;
  return (
    metric.available === true &&
    Math.abs(value - expected) <= Number.EPSILON * Math.max(1, Math.abs(expected)) * 8
  );
}

function platformReconciles(platform, spendBasis) {
  if (!isObject(platform) || !isObject(platform.spend)) return false;
  if (platform.spend.spendBasis !== spendBasis) return false;
  const spend = finiteOrNull(platform.spend.valueUnrounded);
  for (const metric of [platform.cpqv, platform.cpcv]) {
    if (!metricReconciles(metric, spendBasis)) return false;
    const numerator = finiteOrNull(metric.numeratorUnrounded);
    if (metric.available === true && numerator !== spend) return false;
  }
  return true;
}

export function resolveSpendEfficiencyContract(
  payload,
  {
    allowedSpendBases = CLIENT_BILLED_SPEND_BASES,
    platformKey = null,
  } = {}
) {
  const allowed = new Set(allowedSpendBases);
  const selectedPlatformKey = String(platformKey || "").trim().toLowerCase();
  for (const root of rootsFor(payload)) {
    const contract =
      root.contract === SPEND_EFFICIENCY_CONTRACT ? root : root.spendEfficiency;
    if (!isObject(contract) || contract.contract !== SPEND_EFFICIENCY_CONTRACT) continue;
    if (!allowed.has(contract.spendBasis)) continue;
    /*
     * The internal display label is deliberately NOT part of validation.
     * It is presentation, not contract: a client payload withholds it, and
     * comparing against it made the portal silently DISCARD the whole
     * spend-efficiency envelope the moment the server stopped sending
     * internal vocabulary — efficiency metrics would vanish rather than
     * error. The basis token and the numeric reconciliation below are what
     * actually prove the contract.
     */
    if (selectedPlatformKey) {
      const selectedPlatform = contract.byPlatform?.[selectedPlatformKey];
      if (!platformReconciles(selectedPlatform, contract.spendBasis)) continue;
      return contract;
    }
    if (!metricReconciles(contract.blendedCpqv, contract.spendBasis)) continue;
    const platforms = Object.values(contract.byPlatform || {});
    if (!platforms.every((platform) => platformReconciles(platform, contract.spendBasis))) {
      continue;
    }
    return contract;
  }
  return null;
}

export function adaptSpendEfficiencyMetric(metric) {
  if (!isObject(metric)) return null;
  return {
    ...metric,
    numerator: finiteOrNull(metric.numeratorUnrounded),
    denominator: finiteOrNull(metric.denominatorCountUnrounded),
    contributingPlatformKeys: Array.isArray(metric.includedPlatforms)
      ? metric.includedPlatforms
      : [],
    unit: "unit_cost",
    unavailableReason: metric.value == null ? unavailableReason(metric) : null,
    unavailableMessage: metric?.unavailableReason?.message || null,
  };
}

export function resolveSpendEfficiencyMetric(
  contract,
  { platformKey = null, metricKey = "cpqv" } = {}
) {
  if (!isObject(contract)) return null;
  if (!platformKey) {
    return metricKey === "cpqv"
      ? adaptSpendEfficiencyMetric(contract.blendedCpqv)
      : null;
  }
  const platform = contract.byPlatform?.[String(platformKey).toLowerCase()];
  return adaptSpendEfficiencyMetric(platform?.[metricKey]);
}

export function resolveSpendEfficiencySpend(contract, platformKey) {
  const value = contract?.byPlatform?.[String(platformKey || "").toLowerCase()]?.spend
    ?.valueUnrounded;
  return finiteOrNull(value);
}
